import { floorToMonday, floorToMonthStart } from '../misc/helper';

/**
 * bar를 묶는 가상의 단위.
 */
export const chunkSize = 100;

/**
 * chunk를 묶는 가상의 단위.
 * block 단위로 `getIntervalsApi`가 호출된다.
 *
 * `chunkSize`가 100이고 `blockSize`가 5라면, 500개의 bar가 하나의 block을 이룬다.
 */
export const blockSize = 5;

export const secondsPerWeek = 7 * 24 * 60 * 60;

export const secondsPerMonth = 30 * 24 * 60 * 60;

/**
 * 1970년 1월 5일(월요일)의 타임스탬프
 * 1970년 1월 1일(목요일)부터 4일 후
 * 월요일 0시를 기준으로 짜르기 위함
 */
export const firstMondayOffset = 4 * 24 * 60 * 60;

/**
 * 기간을 표현하는 인터페이스입니다.
 * 각 속성은 unix 시간(초 단위)으로 표현됩니다.
 */
export interface Period {
  from: number;
  to: number;
}

/**
 * unix 초를 입력받아서, 그 시간이 속한 chunk의 index를 반환.
 * block의 index이기도 하다.
 * 다만 chunk들 간에는 period가 겹치지 않지만 block들 간에는 period가 겹친다.
 *
 *  ```plaintext
 *  unixSec:                         v <= 이 때 chunkIndex는 6이다. blockIndex는 2, 3, 4, 5, 6 중 하나.
 *  chunks: 0-- 1-- 2-- 3-- 4-- 5-- 6== 7-- 8-- 9-- 10-- ...
 *  blocks: 0------------------ 5================== ...
 *              1------------------ 6================== ...
 *                  2================== 7------------------ ...
 *                      3================== 8------------------ ...
 *                          4================== 9------------------ ...
 *  ```
 */
export function getChunkIndex(unixSec: number, resolution: number): number {
  return Math.floor(unixSec / resolution / chunkSize);
}

export function getChunkPeriod(chunkIndex: number, resolution: number): Period {
  const from = chunkIndex * chunkSize * resolution;
  const to = from + (chunkSize - 1) * resolution;
  return { from, to };
}

export function getBlockPeriod(blockIndex: number, resolution: number): Period {
  const from = blockIndex * chunkSize * resolution;
  const to = from + (chunkSize * blockSize - 1) * resolution;
  return { from, to };
}

/**
 * 주어진 시간을 포함하는 주 블록의 시작 시간을 반환합니다.
 * 이 함수는 getIntervalsApi(/packages/apis/v2/interval/index.ts)에서 차트 데이터를 요청할 때 사용됩니다.
 * 월요일 기준으로 보내면 해당 주에 대한 내용을 포함하여 보내기에 모든 범위를 커버할 수 있습니다
 */
export function getWeekBlockStart(unixSec: number): number {
  const mondayStart = floorToMonday(unixSec);

  const weeksSinceFirstMonday = Math.floor((mondayStart - firstMondayOffset) / secondsPerWeek);
  const blockStartWeekIndex = Math.floor(weeksSinceFirstMonday / chunkSize) * chunkSize;
  return firstMondayOffset + blockStartWeekIndex * secondsPerWeek;
}

export function getWeekBlockPeriod(unixSec: number): Period {
  const from = getWeekBlockStart(unixSec);
  const to = from + secondsPerWeek * chunkSize - 1;

  return { from, to };
}

export function getTimestampFromMonthIndex(monthIndex: number): number {
  if (monthIndex < 0) {
    return 0;
  }

  const year = Math.floor(monthIndex / 12) + 1970;
  const month = monthIndex % 12;
  const date = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));

  return Math.floor(date.getTime() / 1000);
}

export function getMonthIndex(unixSec: number): number {
  if (unixSec < 0) {
    return 0;
  }

  const date = new Date(unixSec * 1000);
  return (date.getUTCFullYear() - 1970) * 12 + date.getUTCMonth();
}

/**
 * 주어진 시간을 포함하는 월 블록의 시작 시간을 반환합니다.
 * 이 함수는 getIntervalsApi(/packages/apis/v2/interval/index.ts)에서 차트 데이터를 요청할 때 사용됩니다.
 * N월 1일로 응답을 보내면 해당 달에 대한 내용을 포함하여 보내기에 모든 범위를 커버할 수 있습니다
 */
export function getMonthBlockStart(unixSec: number): number {
  if (unixSec < 0) {
    return 0;
  }

  const monthIndex = getMonthIndex(floorToMonthStart(unixSec));
  const blockStartMonthIndex = Math.floor(monthIndex / chunkSize) * chunkSize;
  return getTimestampFromMonthIndex(blockStartMonthIndex);
}

export function getMonthBlockPeriod(unixSec: number): Period {
  const from = getMonthBlockStart(unixSec);

  const monthIndex = getMonthIndex(from);
  const endMonthIndex = monthIndex + chunkSize * blockSize - 1;
  const to = getTimestampFromMonthIndex(endMonthIndex);

  return { from, to };
}

export interface BlockStrategy {
  getBlockStart: (timestamp: number) => number;
  getBlockPeriod: (blockStart: number) => Period;
  moveToPrevBlock: (current: number) => number;
  moveToNextBlock: (current: number) => number;
  isValid: (current: number, nextBlock: number) => boolean;
  adjustBlockStartRight: (blockStart: number) => number;
}

export function createDefaultBlockStrategy(resolution: number): BlockStrategy {
  return {
    getBlockStart: (timestamp: number) => getChunkIndex(timestamp, resolution),
    getBlockPeriod: (blockIndex: number) => getBlockPeriod(blockIndex, resolution),
    moveToPrevBlock: (current: number) => current - blockSize,
    moveToNextBlock: (current: number) => current + blockSize,
    isValid: () => true,
    adjustBlockStartRight: (blockStart: number) => blockStart - blockSize + 1,
  };
}

export const weekBlockStrategy: BlockStrategy = {
  getBlockStart: getWeekBlockStart,
  getBlockPeriod: (blockStart: number) => ({
    from: blockStart,
    to: blockStart + secondsPerWeek * (chunkSize * blockSize - 1),
  }),
  moveToPrevBlock: (current: number) => current - secondsPerWeek * chunkSize * blockSize,
  moveToNextBlock: (current: number) => current + secondsPerWeek * chunkSize * blockSize,
  isValid: (current: number, next: number) => !(current === 0 && next === 0),
  adjustBlockStartRight: (blockStart: number) => blockStart,
};

export const monthBlockStrategy: BlockStrategy = {
  getBlockStart: getMonthBlockStart,
  getBlockPeriod: getMonthBlockPeriod,
  moveToPrevBlock: (current: number) => {
    const currentMonthIndex = getMonthIndex(current);
    const prevMonthIndex = currentMonthIndex - chunkSize * blockSize;
    return getTimestampFromMonthIndex(prevMonthIndex);
  },
  moveToNextBlock: (current: number) => {
    const currentMonthIndex = getMonthIndex(current);
    const nextMonthIndex = currentMonthIndex + chunkSize * blockSize;
    return getTimestampFromMonthIndex(nextMonthIndex);
  },
  isValid: (current: number, next: number) => !(current === 0 && next === 0),
  adjustBlockStartRight: (blockStart: number) => blockStart,
};

export function getBlockStrategy(resolution: number): BlockStrategy {
  if (resolution === secondsPerWeek) {
    return weekBlockStrategy;
  } else if (resolution === secondsPerMonth) {
    return monthBlockStrategy;
  } else {
    return createDefaultBlockStrategy(resolution);
  }
}

/**
 * period가 주어지면 그 period를 전부 포함하도록 block들을 왼쪽부터 오른쪽으로 순회합니다.
 *
 *  ```plaintext
 *  # Case 1
 *                   from =>         to
 *                   v---------------v
 *  chunks: 0-- 1-- 2== 3-- 4-- 5-- 6== 7-- 8-- 9-- 10-- ...
 *  blocks: 0------------------ 5------------------ ...
 *              1------------------ 6------------------ ...
 *                  2================== 7------------------ ...
 *                      3------------------ 8------------------ ...
 *                          4------------------ 9------------------ ...
 *  yields: period of block 2
 *
 *  # Case 2
 *                   from =>                 to
 *                   v-----------------------v
 *  chunks: 0-- 1-- 2== 3-- 4-- 5-- 6-- 7-- 8== 9-- 10-- ...
 *  blocks: 0------------------ 5------------------ ...
 *              1------------------ 6------------------ ...
 *                  2================== 7================== ...
 *                      3------------------ 8------------------ ...
 *                          4------------------ 9------------------ ...
 *  yields: period of block 2, period of block 7
 *  ```
 */
export function* iterBlocksToRight(period: Period, resolution: number): Generator<Period> {
  const strategy = getBlockStrategy(resolution);

  let currentBlockStart = strategy.getBlockStart(period.from);

  while (true) {
    const nextBlockPeriod = strategy.getBlockPeriod(currentBlockStart);
    if (nextBlockPeriod.from > period.to) {
      break;
    }
    yield nextBlockPeriod;

    const nextBlockStart = strategy.moveToNextBlock(currentBlockStart);

    if (!strategy.isValid(currentBlockStart, nextBlockStart)) {
      break;
    }

    currentBlockStart = nextBlockStart;
  }
}

/**
 * period가 주어지면 그 period를 전부 포함하도록 block들을 오른쪽부터 왼쪽으로 순회합니다.
 *
 *  ```plaintext
 *  # Case 1
 *                   from         <= to
 *                   v---------------v
 *  chunks: 0-- 1-- 2== 3-- 4-- 5-- 6== 7-- 8-- 9-- 10-- ...
 *  blocks: 0------------------ 5------------------ ...
 *              1------------------ 6------------------ ...
 *                  2================== 7------------------ ...
 *                      3------------------ 8------------------ ...
 *                          4------------------ 9------------------ ...
 *  yields: period of block 2
 *
 *  # Case 2
 *                   from                 <= to
 *                   v-----------------------v
 *  chunks: 0-- 1-- 2== 3-- 4-- 5-- 6-- 7-- 8== 9-- 10-- ...
 *  blocks: 0------------------ 5------------------ ...
 *              1------------------ 6------------------ ...
 *                  2------------------ 7------------------ ...
 *                      3------------------ 8------------------ ...
 *     -1================== 4================== 9------------------ ...
 *  yields: period of block 4, period of block -1
 *  ```
 */
export function* iterBlocksToLeft(period: Period, resolution: number): Generator<Period> {
  const strategy = getBlockStrategy(resolution);

  let currentBlockStart = strategy.adjustBlockStartRight(strategy.getBlockStart(period.to));

  while (true) {
    const nextBlockPeriod = strategy.getBlockPeriod(currentBlockStart);
    if (nextBlockPeriod.to < period.from) {
      break;
    }
    yield nextBlockPeriod;

    const nextBlockStart = strategy.moveToPrevBlock(currentBlockStart);

    if (!strategy.isValid(currentBlockStart, nextBlockStart)) {
      break;
    }

    currentBlockStart = nextBlockStart;
  }
}
